import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, Inbox, Phone, Mail, MapPin, FileText, Trash2, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { leadsApi } from '../api';
import { formatPhoneNumber } from '../components/PhoneInput';

const TYPE_LABELS = {
  'kitchen-remodel': 'Kitchen Remodeling', 'bathroom-remodel': 'Bathroom Renovation',
  'addition': 'Home Addition', 'basement': 'Basement Finishing',
  'roofing-siding': 'Roofing & Siding', 'interior': 'Interior Remodeling', 'other': 'Other',
};
const BUDGET_LABELS = {
  'under-100k': 'Under $100,000', '100k-250k': '$100,000 – $250,000', '250k-500k': '$250,000 – $500,000',
  '500k-1m': '$500,000 – $1,000,000', 'over-1m': 'Over $1,000,000',
};
const TIMELINE_LABELS = {
  'asap': 'As soon as possible', '1-3months': '1–3 months', '3-6months': '3–6 months',
  '6-12months': '6–12 months', 'flexible': 'Flexible',
};

const Field = ({ label, value }) => value ? (
  <div>
    <div className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-0.5">{label}</div>
    <div className="text-sm text-gray-200">{value}</div>
  </div>
) : null;

const LeadCard = ({ lead, expanded, onToggle, onConvert, onDelete, busy }) => {
  const isNew = !lead.is_viewed;
  return (
    <Card
      className={`bg-[#111111] p-0 overflow-hidden transition-colors ${isNew ? 'border-[#D4AF37]/60 border-l-4 border-l-[#D4AF37]' : 'border-gray-800'}`}
      data-testid={`lead-card-${lead.id}`}
    >
      <button onClick={onToggle} className="w-full text-left px-4 py-3.5 flex items-center gap-3 hover:bg-[#161616] transition-colors" data-testid={`lead-toggle-${lead.id}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold">{lead.name}</span>
            {isNew && <span className="text-[9px] font-brand font-bold uppercase tracking-wider bg-[#D4AF37] text-black rounded px-1.5 py-0.5" data-testid={`lead-new-badge-${lead.id}`}>New</span>}
            {lead.converted_quote_id && <span className="text-[9px] font-brand font-bold uppercase tracking-wider bg-green-900/40 text-green-400 border border-green-800 rounded px-1.5 py-0.5">Converted</span>}
          </div>
          <p className="text-xs text-gray-400 mt-0.5 truncate">
            {TYPE_LABELS[lead.project_type] || lead.project_type || 'Project'}
            {lead.address && <> · {lead.address}</>}
          </p>
        </div>
        <span className="text-[11px] text-gray-500 shrink-0">{new Date(lead.created_at).toLocaleDateString()}</span>
        {expanded ? <ChevronUp size={16} className="text-gray-500 shrink-0" /> : <ChevronDown size={16} className="text-gray-500 shrink-0" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-800 pt-4" data-testid={`lead-detail-${lead.id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {lead.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-200"><Phone size={14} className="text-[#D4AF37]" /><a href={`tel:${lead.phone}`} className="hover:text-[#D4AF37]">{formatPhoneNumber(lead.phone)}</a></div>
            )}
            {lead.email && (
              <div className="flex items-center gap-2 text-sm text-gray-200"><Mail size={14} className="text-[#D4AF37]" /><a href={`mailto:${lead.email}`} className="hover:text-[#D4AF37] break-all">{lead.email}</a></div>
            )}
            {lead.address && (
              <div className="flex items-center gap-2 text-sm text-gray-200 sm:col-span-2"><MapPin size={14} className="text-[#D4AF37]" />{lead.address}</div>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <Field label="Project Type" value={TYPE_LABELS[lead.project_type] || lead.project_type} />
            <Field label="Size" value={lead.project_size ? `${lead.project_size} sq ft` : null} />
            <Field label="Budget" value={BUDGET_LABELS[lead.budget] || lead.budget} />
            <Field label="Timeline" value={TIMELINE_LABELS[lead.timeline] || lead.timeline} />
          </div>
          {lead.description && (
            <div className="mt-4">
              <div className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37] mb-1">Project Description</div>
              <p className="text-sm text-gray-300 whitespace-pre-wrap bg-black/40 border border-gray-800 rounded p-3">{lead.description}</p>
            </div>
          )}
          <p className="text-[11px] text-gray-600 mt-3">Submitted {new Date(lead.created_at).toLocaleString()}{lead.viewed_at && <> · First viewed {new Date(lead.viewed_at).toLocaleString()}</>}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {lead.converted_quote_id ? (
              <Button size="sm" onClick={() => onConvert(lead)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid={`open-quote-btn-${lead.id}`}>
                <ExternalLink size={13} /> Open Quote {lead.converted_quote_number}
              </Button>
            ) : (
              <Button size="sm" onClick={() => onConvert(lead)} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid={`convert-lead-btn-${lead.id}`}>
                <FileText size={13} /> Convert to Quote
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onDelete(lead)} disabled={busy} className="border-red-900 text-red-400 hover:text-red-300" data-testid={`delete-lead-btn-${lead.id}`}>
              <Trash2 size={13} /> Delete
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default function OpsLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => leadsApi.list().then((r) => setLeads(r.data)).catch(() => toast.error('Could not load leads'));
  useEffect(() => { load(); }, []);

  const toggle = (lead) => {
    const opening = expandedId !== lead.id;
    setExpandedId(opening ? lead.id : null);
    if (opening && !lead.is_viewed) {
      leadsApi.markViewed(lead.id).then((r) => {
        setLeads((ls) => ls.map((l) => (l.id === lead.id ? r.data : l)));
        window.dispatchEvent(new Event('leads-updated'));
      }).catch(() => {});
    }
  };

  const convert = async (lead) => {
    if (lead.converted_quote_id) return navigate(`/ops/quotes/${lead.converted_quote_id}`);
    setBusy(true);
    try {
      const r = await leadsApi.convert(lead.id);
      toast.success(`Quote ${r.data.quote_number} created from this lead`);
      window.dispatchEvent(new Event('leads-updated'));
      navigate(`/ops/quotes/${r.data.quote_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not convert lead');
    } finally { setBusy(false); }
  };

  const doDelete = async (lead) => {
    setBusy(true);
    try {
      await leadsApi.delete(lead.id);
      toast.success('Lead deleted');
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      setConfirmDelete(null);
      window.dispatchEvent(new Event('leads-updated'));
    } catch { toast.error('Could not delete lead'); } finally { setBusy(false); }
  };

  if (leads === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div>;

  const newCount = leads.filter((l) => !l.is_viewed).length;

  return (
    <div data-testid="ops-leads-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-white">Website Leads</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            Quote requests submitted on BuildBroz.com{newCount > 0 && <span className="text-[#D4AF37]"> · {newCount} new</span>}
          </p>
        </div>
      </div>

      {leads.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-10 text-center" data-testid="leads-empty-state">
          <Inbox size={36} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No leads yet. When a customer requests a quote on the website, it will show up here.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {leads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} expanded={expandedId === lead.id} busy={busy}
              onToggle={() => toggle(lead)} onConvert={convert} onDelete={setConfirmDelete} />
          ))}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="delete-lead-dialog">
          <Card className="bg-[#111111] border-gray-800 p-5 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">Delete this lead?</h3>
            <p className="text-sm text-gray-400 mb-4">The quote request from <b className="text-white">{confirmDelete.name}</b> will be permanently deleted.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={busy} className="border-gray-700 text-gray-300">Cancel</Button>
              <Button onClick={() => doDelete(confirmDelete)} disabled={busy} className="bg-red-700 hover:bg-red-600 text-white font-semibold" data-testid="confirm-delete-lead-btn">
                {busy && <Loader2 size={14} className="animate-spin" />} Delete
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
